import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, readOfflineSnapshot, writeOfflineSnapshot } from "./api";
import type { Session } from "./types";
import { AppShell } from "./ui/AppShell";
import { LoginPage } from "./pages/LoginPage";
import { TodayPage } from "./pages/TodayPage";
import { DeparturePage } from "./pages/DeparturePage";
import { GuestPage } from "./pages/GuestPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SetupPage } from "./pages/SetupPage";
import { StaffSetupPage } from "./pages/StaffSetupPage";
import { PeoplePage } from "./pages/PeoplePage";
import { ReportsPage } from "./pages/ReportsPage";
import { HelpPage } from "./pages/HelpPage";
import { SessionProvider, useSession } from "./SessionContext";
import { useOnlineStatus } from "./useOnlineStatus";
import { useI18n } from "./i18n";

function AuthenticatedLayout() {
  const { t } = useI18n();
  const location = useLocation();
  const online = useOnlineStatus();
  const cachedSession = readOfflineSnapshot<Session>("session");
  const session = useQuery({ queryKey: ["session"], queryFn: () => api<Session>("/api/v1/foundation/session"), retry: false, enabled: online, initialData: cachedSession?.value, initialDataUpdatedAt: 0 });
  useEffect(() => { if (online && session.data) writeOfflineSnapshot("session", session.data); }, [online, session.data]);
  if (session.isLoading) return <div className="full-state"><div className="spinner" aria-label={t("loading")} /></div>;
  if (!online && !session.data) return <div className="full-state"><div className="error-block">{t("offlineUnavailable")}</div></div>;
  if (!session.data) return <Navigate to="/login" state={{ from: location }} replace />;
  return <SessionProvider session={session.data}><AppShell session={session.data}><Outlet /></AppShell></SessionProvider>;
}

function RequirePermission({ permission, children }: { permission: string; children: ReactNode }) {
  const session = useSession();
  return session.permissions.includes(permission) ? children : <Navigate to="/" replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/guest/:token" element={<GuestPage />} />
      <Route path="/staff-setup/:token" element={<StaffSetupPage />} />
      <Route element={<AuthenticatedLayout />}>
        <Route index element={<TodayPage />} />
        <Route path="/departures" element={<TodayPage />} />
        <Route path="/departures/:departureId" element={<DeparturePage />} />
        <Route path="/settings" element={<RequirePermission permission="center:configure"><SettingsPage /></RequirePermission>} />
        <Route path="/setup" element={<RequirePermission permission="center:configure"><SetupPage /></RequirePermission>} />
        <Route path="/people" element={<RequirePermission permission="participant:write"><PeoplePage /></RequirePermission>} />
        <Route path="/reports" element={<RequirePermission permission="export:create"><ReportsPage /></RequirePermission>} />
        <Route path="/help" element={<HelpPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
